<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="catTeal" class="mr-2 flex-shrink-0">
        {{ icon || "mdi-cup-water" }}
      </v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <v-chip
        v-if="latest && latest.liters_per_day != null"
        x-small
        color="cyan darken-2"
        text-color="white"
        class="ml-2 flex-shrink-0"
      >
        {{ litersLabel(latest.liters_per_day) }}
      </v-chip>
      <!-- salvia-781/878: atalho de inserção, sempre visível. Abre o diálogo
           direto no formulário; o botão ao lado abre o histórico. -->
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" class="flex-shrink-0 ml-1" @click="openDialog">
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver e editar hidratação</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <div v-if="!latest" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-cup-off</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhum registro de hidratação.
        </p>
        <v-btn small color="catTeal" outlined class="mt-3" @click="openQuickAdd">
          <v-icon small left>mdi-plus</v-icon>
          Registrar hidratação
        </v-btn>
      </div>

      <div v-else class="info-list">
        <div class="info-row">
          <span class="info-label">Água por dia</span>
          <span class="info-value">
            {{ latest.liters_per_day != null ? litersLabel(latest.liters_per_day) : latest.notes || "—" }}
          </span>
        </div>
        <div v-if="latest.liters_per_day != null && latest.notes" class="info-row">
          <span class="info-label">Resposta</span>
          <span class="info-value">{{ latest.notes }}</span>
        </div>
      </div>
    </v-card-text>

    <EditHydrationDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :records="records"
      :patient-id="patientId"
      @data-saved="handleDataSaved"
      @data-deleted="handleDataChanged"
    />
  </v-card>
</template>

<script>
import { getHydrationByPatient } from "@/services/hydrationService";
import EditHydrationDialog from "@/features/patients/profile-dashboard/dialogs/EditHydrationDialog.vue";

import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";

import dashboardStore from "@/features/patients/profile-dashboard/patientDashboardStore";
import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "HydrationCard",

  components: {
    EditHydrationDialog,
  },

  props: {
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: null },
  },

  data() {
    return { dialogVisible: false, records: [] };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.records || []).length;
    },
    latest() {
      return this.records.length ? this.records[0] : null;
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    async fetchData() {
      if (!this.patientId) return;
      try {
        this.records = await dashboardStore.section(this.patientId, "hydration", () => getHydrationByPatient(this.patientId));
      } catch (error) {
        console.error("Erro ao buscar hidratação:", error);
        this.records = [];
      }
    },

    litersLabel(v) {
      if (v == null) return "";
      const n = Number(v);
      const s = Number.isInteger(n) ? String(n) : n.toFixed(1);
      return `${s} L/dia`;
    },

    openDialog() {
      this.dialogVisible = true;
    },

    async handleDataChanged() {
      await this.fetchData();
      this.$emit("data-updated");
    },
  },
};
</script>

<style scoped>
/* ═══ Tema: Ciano (Hidratação) ═══ */
.card-fixed-height {
  display: flex;
  flex-direction: column;
  height: 320px;
  border-radius: 14px !important;
  border: 1px solid #a5f3fc !important;
  box-shadow: 0 2px 10px rgba(14, 116, 144, 0.1) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  border-color: #67e8f9 !important;
  box-shadow: 0 6px 20px rgba(14, 116, 144, 0.18) !important;
}

.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: linear-gradient(135deg, #ecfeff 0%, #cffafe 100%);
  border-bottom: 1px solid #a5f3fc;
}

.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ds-cat-teal);
  letter-spacing: 0.3px;
}

.card-content {
  flex: 1;
  overflow-y: auto;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  border-bottom: 1px dashed #cffafe;
  padding-bottom: 8px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-cat-teal);
  flex-shrink: 0;
}

.info-value {
  font-size: 13px;
  color: var(--ds-text-600);
  text-align: right;
}
</style>
